import {
  WARRANTY_REVISION_MONTHS,
  isVehicleSize,
  type PackageSizePrices,
  type VehicleSize,
} from '@mustauto/types/esthetic-packages';
import { formatDateBR } from './br/date';

/**
 * Regras puras dos pacotes de estética (Fase 2): preço pelo porte e o
 * calendário da garantia. App, painel e API calculam aqui — o preço que o
 * mecânico vê na abertura é o preço que entra na OS.
 */

/**
 * O preço do pacote para o porte, em REAIS. Porte sem preço próprio (ou porte
 * não informado) cai no preço base — é o que faz o pacote cadastrado com um
 * preço só continuar funcionando.
 */
export function packagePriceForSize(
  basePrice: number,
  sizePrices: PackageSizePrices | null | undefined,
  size: VehicleSize | null | undefined,
): number {
  if (size && sizePrices) {
    const price = sizePrices[size];
    if (typeof price === 'number' && Number.isFinite(price) && price >= 0) return price;
  }
  return basePrice;
}

/**
 * Lê o jsonb `size_prices` sem confiar nele: só portes conhecidos, só número
 * finito e não negativo. Linha antiga (nulo) ou lixo viram `{}`.
 */
export function readPackageSizePrices(raw: unknown): PackageSizePrices {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: PackageSizePrices = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!isVehicleSize(key)) continue;
    const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
    if (Number.isFinite(n) && n >= 0) out[key] = n;
  }
  return out;
}

/** Lê o jsonb `included_items`: só textos não vazios. */
export function readIncludedItems(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is string => typeof v === 'string' && v.trim().length > 0).map((v) => v.trim());
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Soma meses a uma data pura. Dia que não existe no mês de destino cai no
 * último dia dele: vitrificação feita em 31/08 revisa em 28/02, não em 03/03.
 */
export function addMonthsIsoDate(iso: string, months: number): string {
  const m = ISO_DATE.exec(iso);
  if (!m) return iso;
  const year = Number(m[1]);
  const month = Number(m[2]) - 1 + months;
  const day = Number(m[3]);
  const targetYear = year + Math.floor(month / 12);
  const targetMonth = ((month % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  const d = new Date(Date.UTC(targetYear, targetMonth, Math.min(day, lastDay)));
  return d.toISOString().slice(0, 10);
}

/**
 * Até quando vale a garantia: o dia da conclusão + os meses do pacote. Data
 * pura (Regra 13): quem passa o dia da conclusão já o converteu no fuso da
 * oficina. Sem garantia ou sem conclusão, `null`.
 */
export function warrantyUntil(completedIsoDate: string | null | undefined, months: number | null | undefined): string | null {
  if (!completedIsoDate || !months || months <= 0) return null;
  return addMonthsIsoDate(completedIsoDate, months);
}

export interface WarrantyRevision {
  /** Meses depois da conclusão (6, 12). */
  months: number;
  /** O dia da revisão, data pura. */
  date: string;
}

/**
 * As revisões que cabem na garantia — ver `WARRANTY_REVISION_MONTHS`. A de 12
 * meses numa garantia de 12 cai no último dia dela: é a revisão que a renova.
 */
export function warrantyRevisions(
  completedIsoDate: string | null | undefined,
  months: number | null | undefined,
): WarrantyRevision[] {
  if (!completedIsoDate || !months || months <= 0) return [];
  return WARRANTY_REVISION_MONTHS.filter((m) => m <= months).map((m) => ({
    months: m,
    date: addMonthsIsoDate(completedIsoDate, m),
  }));
}

/** "12 meses", "1 mês", "5 anos". */
export function formatWarrantyMonths(months: number): string {
  if (months >= 12 && months % 12 === 0) {
    const years = months / 12;
    return years === 1 ? '1 ano' : `${years} anos`;
  }
  return months === 1 ? '1 mês' : `${months} meses`;
}

/** O que a abertura precisa saber de um pacote escolhido. */
export interface PackageForOpening {
  id: string;
  serviceName: string;
  basePrice: number;
  sizePrices?: PackageSizePrices | null;
  durationHours?: number | null;
  warrantyMonths?: number | null;
}

/** O item de mão de obra que o pacote vira na OS — mesma forma do item do app. */
export interface PackageLaborItem {
  id: string;
  type: 'labor';
  description: string;
  billingMode: 'fixed';
  quantity: 1;
  /** CENTAVOS, como todo item da OS. */
  unitPrice: number;
  total: number;
  internalNotes: null;
  createdAt: number;
  /** O pacote de origem — referência, não trava: o preço já está no item. */
  packageId: string;
  /**
   * Garantia do pacote, em meses, quando há. É por ela que o lembrete de
   * revisão nomeia o serviço certo ("Vitrificação", e não "Lavagem").
   */
  warrantyMonths?: number;
}

/**
 * O item que o pacote vira na OS, com o preço do porte (F2.2). O preço é
 * COPIADO: o gestor que reajusta o pacote amanhã não muda a OS aberta hoje.
 * Mesma função no app (abertura offline) e na API (abertura pelo painel).
 */
export function packageLaborItem(
  pkg: PackageForOpening,
  size: VehicleSize | null | undefined,
  itemId: string,
  now: number = Date.now(),
): PackageLaborItem {
  const cents = Math.round(packagePriceForSize(pkg.basePrice, pkg.sizePrices, size) * 100);
  return {
    id: itemId,
    type: 'labor',
    description: pkg.serviceName,
    billingMode: 'fixed',
    quantity: 1,
    unitPrice: cents,
    total: cents,
    internalNotes: null,
    createdAt: now,
    packageId: pkg.id,
    ...(pkg.warrantyMonths && pkg.warrantyMonths > 0 ? { warrantyMonths: pkg.warrantyMonths } : {}),
  };
}

/**
 * A garantia da OS com vários pacotes: a MAIOR. Vitrificação (12 meses) com
 * higienização (sem garantia) é uma OS com 12 meses — a revisão é da
 * vitrificação, e é ela que traz o cliente de volta.
 */
export function packagesWarrantyMonths(pkgs: readonly PackageForOpening[]): number | null {
  const months = pkgs.map((p) => p.warrantyMonths ?? 0).filter((m) => m > 0);
  return months.length > 0 ? Math.max(...months) : null;
}

/** Horas somadas dos pacotes — a base do prazo sugerido. Nenhuma duração = `null`. */
export function packagesDurationHours(pkgs: readonly PackageForOpening[]): number | null {
  const hours = pkgs.map((p) => p.durationHours ?? 0).filter((h) => h > 0);
  return hours.length > 0 ? hours.reduce((a, b) => a + b, 0) : null;
}

/** Duração do pacote como o detailer fala: "3 h", "1 h 30", "2 dias". */
export function formatPackageHours(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) return '';
  if (hours >= 24 && hours % 24 === 0) {
    const days = hours / 24;
    return days === 1 ? '1 dia' : `${days} dias`;
  }
  const whole = Math.floor(hours);
  const minutes = Math.round((hours - whole) * 60);
  if (whole === 0) return `${minutes} min`;
  return minutes > 0 ? `${whole} h ${String(minutes).padStart(2, '0')}` : `${whole} h`;
}

/**
 * A garantia da OS como a API a manda (`osWarranty`) e toda tela a lê —
 * painel, app do gestor, app do cliente. Datas puras `YYYY-MM-DD`.
 */
export interface OSWarranty {
  months: number;
  /** Até quando vale; nulo enquanto a OS não foi concluída. */
  until: string | null;
  /** As revisões que cabem nela (6 e 12 meses), com o dia de cada uma. */
  revisions: WarrantyRevision[];
}

/** "Garantia até 25/09/2027" — ou "Garantia de 12 meses" enquanto ainda não começou. */
export function warrantyHeadline(w: OSWarranty): string {
  return w.until ? `Garantia até ${formatDateBR(w.until)}` : `Garantia de ${formatWarrantyMonths(w.months)}`;
}

/** "25/03/2027 e 25/09/2027" — os dias das revisões — ou `''`. */
export function warrantyRevisionDates(w: OSWarranty): string {
  return w.revisions.map((r) => formatDateBR(r.date)).join(' e ');
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A garantia de uma resposta da API, sem confiar na forma — cache antigo, API
 * anterior à F2.5, payload torto. O que não é garantia de verdade vira `null`;
 * revisão sem mês ou sem dia válido sai da lista, em vez de virar "revisões  e".
 */
export function parseOSWarranty(raw: unknown): OSWarranty | null {
  if (!raw || typeof raw !== 'object') return null;
  const w = raw as { months?: unknown; until?: unknown; revisions?: unknown };
  const months = typeof w.months === 'number' ? w.months : NaN;
  if (!Number.isInteger(months) || months <= 0) return null;
  const revisions: WarrantyRevision[] = Array.isArray(w.revisions)
    ? w.revisions.flatMap((r: unknown) => {
        const rev = r as { months?: unknown; date?: unknown } | null;
        return rev && typeof rev.months === 'number' && rev.months > 0 && typeof rev.date === 'string' && ISO_DAY.test(rev.date)
          ? [{ months: rev.months, date: rev.date }]
          : [];
      })
    : [];
  const until = typeof w.until === 'string' && ISO_DAY.test(w.until) ? w.until : null;
  return { months, until, revisions };
}
